import clsx from "clsx";

import { fieldFallbackLabel } from "@smart/bridge-intake-components-dom";
import {
  Button,
  DragHandleProps,
  EditableText,
  Icon,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { UpdatedSection } from "../../types";

type MovableSectionProps = {
  index: number;
  item: UpdatedSection;
  dragHandleProps: DragHandleProps;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  setTitle: (index: number, title: string) => void;
  setDeleted: (index: number, deleted: boolean) => void;
  numOfSections: number;
  newSectionUri?: string;
  isDragging: boolean;
  copySection: (index: number) => void;
};

const MovableSection = ({
  index,
  item,
  dragHandleProps,
  moveUp,
  moveDown,
  setTitle,
  setDeleted,
  numOfSections,
  newSectionUri,
  isDragging,
  copySection,
}: MovableSectionProps) => {
  const draggable = numOfSections > 1;
  return (
    <div
      className={clsx(
        "flex flex-wrap items-center gap-small p-[0.8rem] pr-[1.6rem] bg-white border rounded border-solid border-neutral-200",
        item.deleted && "hidden",
        isDragging && "opacity-50",
      )}
    >
      <div
        {...dragHandleProps.attributes}
        {...dragHandleProps.listeners}
        className={clsx(
          "flex items-center",
          !draggable && "pointer-events-none invisible",
        )}
      >
        <Icon name="regularGripDotsVertical" />
      </div>
      <div className="flex-1 text-ellipsis overflow-hidden">
        <EditableText
          text={item.title}
          placeholder={fieldFallbackLabel.section}
          editing={newSectionUri ? newSectionUri === item.uri : undefined}
          onChange={(value) => setTitle(index, value)}
          onComplete={async () => {}}
        />
      </div>
      <div className="flex gap-small">
        <Button
          variant="secondarySubtle"
          leftIcon="regularAngleUp"
          dataTestId={`move-up-button-${item.uri}`}
          disabled={index === 0}
          onClick={() => moveUp(index)}
        />
        <Button
          variant="secondarySubtle"
          leftIcon="regularAngleDown"
          dataTestId={`move-down-button-${item.uri}`}
          disabled={index + 1 === numOfSections}
          onClick={() => moveDown(index)}
        />
        <Tooltip
          title="Duplicate Section"
          text="Mapped fields and conditions are cleared, add new ones to the copied section"
          variant="paragraphy"
          mountTo="body"
          placement="bottom-end"
          floatingClassName="z-100"
        >
          <Button
            variant="secondarySubtle"
            leftIcon="regularCopy"
            dataTestId={`copy-section-button-${item.uri}`}
            onClick={() => copySection(index)}
          />
        </Tooltip>
        <Tooltip
          title="Delete Section"
          text="Click to delete section"
          variant="paragraphy"
          mountTo="body"
          placement="bottom-end"
          floatingClassName="z-100"
        >
          <Button
            variant="secondarySubtle"
            leftIcon="regularTrashCan"
            dataTestId={`delete-section-button-${item.uri}`}
            onClick={() => setDeleted(index, true)}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export { MovableSection };
